import React, { type CSSProperties, useEffect, useReducer } from 'react';
import classnames from 'src/util/classnames';
import { getBundledPhosphorExport, getPhosphorIcon, PhosphorWeight } from 'src/util/icons/phosphor';
import { getLoadedPack, loadPack, parseReactIconName, ReactIconRef } from 'src/util/icons/reactIconPacks';

// Patterns that indicate a class-based (Font Awesome / custom) icon string.
// These are strings that contain spaces or start with known icon class prefixes.
const FA_PREFIXES = /^(fa[sbrldc]?|glyphicon|icon)\s/i;
const FA_DASH    = /\bfa-/;

function isFontAwesome(icon: string): boolean {
  return FA_PREFIXES.test(icon) || FA_DASH.test(icon);
}

// Font Awesome 6 icons (`fa6-House`) are no longer bundled; render saved ones
// with the Font Awesome 5 class of the same name, which covers most of them.
const RETIRED_FA6 = /^fa6-([A-Z0-9][A-Za-z0-9]*)$/;

function retiredFa6Class(icon: string): string | null {
  const match = RETIRED_FA6.exec(icon);
  if (!match) return null;

  return `fa fa-${match[1].replace(/(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Za-z])(?=[0-9])/g, '-').toLowerCase()}`;
}

// Phosphor's regular and duotone weights are bundled, by bare name or as
// `phosphor-House` / `phosphor-HouseDuotone`; null when the icon must download.
function bundledPhosphorIcon(icon: string, iconRef: ReactIconRef | null, weight: PhosphorWeight) {
  if (!iconRef) return getPhosphorIcon(icon, weight);

  return iconRef.pack.alias === 'phosphor' ? getBundledPhosphorExport(iconRef.exportName) : null;
}

interface ReactPackIconProps {
  iconRef: ReactIconRef;
  size: number | string;
  className?: string;
  style?: CSSProperties;
  color?: string;
  rest: Record<string, unknown>;
}

// Renders a react-icons icon, downloading its library chunk on first use.
// Holds an empty box of the final size meanwhile so layout does not shift.
function ReactPackIcon({ iconRef, size, className, style, color, rest }: ReactPackIconProps) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const module = getLoadedPack(iconRef.pack);

  useEffect(() => {
    if (module) return undefined;
    let cancelled = false;
    loadPack(iconRef.pack)
      .then(() => !cancelled && rerender())
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [module, iconRef.pack]);

  if (!module) {
    return <span className={className} style={{ display: 'inline-block', width: size, height: size, ...style }} aria-hidden />;
  }

  const PackIcon = module[iconRef.exportName];
  if (!PackIcon) return null;

  return (
    <PackIcon
      size={size}
      className={className}
      style={style}
      color={color}
      {...(rest as React.SVGAttributes<SVGElement>)}
      aria-hidden
    />
  );
}

export interface IconProps {
  /**
   * Font Awesome 5 class string ("fa fa-home"), Phosphor duotone name ("House"),
   * or a react-icons name prefixed with its library alias ("phosphor-HouseBold").
   * Legacy Lucide names ("Trash2") still resolve to their Phosphor equivalent.
   */
  icon?: string | null;
  /** Weight for a bare Phosphor name: duotone (the default) or regular. Ignored for other icons. */
  weight?: PhosphorWeight;
  /** Pixel size forwarded to SVG icons; used as fontSize for FA icons. */
  size?: number | string;
  /** Additional CSS classes. */
  className?: string;
  style?: CSSProperties;
  color?: string;
  /** Alias for size — kept for compatibility with existing callers. */
  fontSize?: number | string;
  [key: string]: unknown;
}

/**
 * Unified icon component that renders either:
 *  - A Font Awesome icon:       <Icon icon="fa fa-home" />
 *  - A Phosphor duotone icon:  <Icon icon="House" size={16} />
 *  - A regular-weight icon:    <Icon icon="House" weight="regular" />
 *  - A react-icons icon:       <Icon icon="phosphor-HouseBold" size={16} />
 *
 * Detection:
 *  - If `icon` contains spaces or matches FA/custom-icon prefixes → FA/class-based
 *  - If `icon` is `fa6-<Name>` (retired library) → the Font Awesome 5 class of that name
 *  - If `icon` is `<alias>-<Name>` for a known library → react-icons component (bundled when Phosphor regular/duotone)
 *  - If `icon` is a Phosphor (or legacy Lucide) name → Phosphor component in `weight` (duotone by default)
 *  - Otherwise → falls back to <i className={icon} />
 */
export function Icon({ icon, weight = 'duotone', size, fontSize, className, style, color, ...rest }: IconProps) {
  if (!icon) return null;

  const resolvedSize = size ?? fontSize;

  // ── Font Awesome / class-based icon ──────────────────────────────────────
  if (isFontAwesome(icon) || icon.includes(' ')) {
    return (
      <i
        className={classnames('fa', icon, className)}
        style={{ fontSize: resolvedSize, color, ...style }}
        {...(rest as React.HTMLAttributes<HTMLElement>)}
        aria-hidden
      />
    );
  }

  const fa6Class = retiredFa6Class(icon);
  if (fa6Class) {
    return (
      <i
        className={classnames(fa6Class, className)}
        style={{ fontSize: resolvedSize, color, ...style }}
        {...(rest as React.HTMLAttributes<HTMLElement>)}
        aria-hidden
      />
    );
  }

  // ── react-icons icon (prefixed) ───────────────────────────────────────────
  const reactIconRef = parseReactIconName(icon);
  const PhosphorIcon = bundledPhosphorIcon(icon, reactIconRef, weight);

  if (PhosphorIcon) {
    return (
      <PhosphorIcon
        size={resolvedSize ?? 16}
        className={className}
        style={style}
        color={color}
        {...(rest as React.SVGAttributes<SVGElement>)}
        aria-hidden
      />
    );
  }

  if (reactIconRef) {
    return (
      <ReactPackIcon iconRef={reactIconRef} size={resolvedSize ?? 16} className={className} style={style} color={color} rest={rest} />
    );
  }

  // ── Fallback: unknown class string ────────────────────────────────────────
  return (
    <i
      className={classnames(icon, className)}
      style={{ fontSize: resolvedSize, color, ...style }}
      {...(rest as React.HTMLAttributes<HTMLElement>)}
      aria-hidden
    />
  );
}

export default Icon;
